import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  getInterventionList,
  getTreatmentListByIntervention,
  setDressingAppearance,
  setDebridement,
  setWoundCleanserGetData,
  setSkinProtectantGetData,
  setPrimaryDressingGetData,
  setSecondaryDressingGetData,
  setCompressionGetData,
  setModalities,
  setAdditionalCare,
  setdressChangeFrequency,
  setInterventionListOptions,
  setTreatmentListIntervention,
  setSelectedInterventionId,
  setSelectedInterventionName,
} from "../../../../../app/patientSlice";
import { LOADING_KEYS } from "../../../../../constants/loadingKeys";
import { useLoading } from "../../../../../hooks/useLoading";
import { assessmentRbacChecker } from "../../../../../utils/rbac";

export const useTreatment = () => {
  const dispatch = useDispatch();
  const { patientId, assessmentId } = useParams();

  const userDetails = useSelector((state) => state.user);
  const patientData = useSelector((state) => state.patient.detailViewData);
  const assessmentDetails = useSelector((state) => state.patient.detailAssessment);
  const listOptions = useSelector((state) => state.patient.interventionListOptions);
  const selectedInterventionId = useSelector((state) => state.patient.selectedInterventionId);
  const selectedInterventionName = useSelector((state) => state.patient.selectedInterventionName);
  const dynTab = useSelector((state) => state.patient.treatmentListIntervention);
  const interventionLoader = useLoading(LOADING_KEYS.INTERVENTION_TREATMENT);
  const hideButtons = assessmentRbacChecker(userDetails?.role, patientData?.isOwnCustomer);

  const [viewDropDown, setDropDown] = useState(false);
  const [defaultIndex, setDefaultIndex] = useState("");
  const [formularyId, setFormularyId] = useState(patientData?.formularyId);
  const dropDownRef = useRef();

  const resetTreatmentState = useCallback(() => {
    dispatch(setDressingAppearance({}));
    dispatch(setDebridement({}));
    dispatch(setWoundCleanserGetData({}));
    dispatch(setSkinProtectantGetData({}));
    dispatch(setPrimaryDressingGetData({}));
    dispatch(setSecondaryDressingGetData({}));
    dispatch(setCompressionGetData({}));
    dispatch(setModalities({}));
    dispatch(setAdditionalCare({}));
    dispatch(setdressChangeFrequency({}));
  }, [dispatch]);

  useEffect(() => {
    dispatch(setInterventionListOptions([]));
    dispatch(setTreatmentListIntervention({}));
    resetTreatmentState();
  }, [dispatch, assessmentId]);

  useEffect(() => {
    if (assessmentId) {
      dispatch(getInterventionList({ patientId, assessmentId }));
    }
  }, [dispatch, assessmentId, patientId]);

  useEffect(() => {
    if (patientData?.formularyId) {
      setFormularyId(patientData.formularyId);
    }
  }, [patientData?.formularyId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropDownRef.current && !dropDownRef.current.contains(e.target)) {
        setDropDown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (selectedInterventionId && selectedInterventionId !== 0) {
      dispatch(getTreatmentListByIntervention({ patientId, assessmentId, interventionId: selectedInterventionId }));
    }
  }, [selectedInterventionId, dispatch, patientId, assessmentId]);

  const handleDropDown = useCallback(() => setDropDown((prev) => !prev), []);

  const handleDropChange = useCallback(
    (e, i, optionValues) => {
      if (optionValues) {
        if (optionValues.interventionId === selectedInterventionId) {
          setDropDown(false);
          return;
        }
        resetTreatmentState();
        dispatch(setTreatmentListIntervention({}));
        dispatch(setSelectedInterventionName(optionValues.interventionName));
        dispatch(setSelectedInterventionId(optionValues.interventionId));
        setDefaultIndex(0);
      }
      setDropDown(false);
    },
    [dispatch, resetTreatmentState, selectedInterventionId]
  );

  const handleClickAddIntervention = useCallback(() => {
    if (!assessmentId) return;
    dispatch(setTreatmentListIntervention({}));
    resetTreatmentState();
    dispatch(setSelectedInterventionId(0));
    dispatch(setSelectedInterventionName("Intervention"));
  }, [dispatch, assessmentId, resetTreatmentState]);

  const isInterventionComplete = useMemo(() => Array.isArray(dynTab) && dynTab.some((d) => d.isCompleted === 1), [dynTab]);

  return {
    patientData,
    assessmentDetails,
    interventionLoader,
    listOptions,
    selectedInterventionId,
    selectedInterventionName,
    dynTab,
    hideButtons,
    viewDropDown,
    defaultIndex,
    formularyId,
    dropDownRef,
    isInterventionComplete,
    handleDropDown,
    handleDropChange,
    handleClickAddIntervention,
  };
};
